/**
 * 分享初始化器 - 确保微信分享在各个阶段都能正确配置
 * 解决分享显示为链接而不是卡片的问题
 */

import { isWechatBrowser } from './wechatEnvironment'
import { setupWechatShareWithRetry } from './wechatShareEnhanced'
import { updateMetaTags } from './metaTags'
import { getPageSpecificDefaultConfig } from './shareConfig'

/**
 * 默认分享配置
 */

/**
 * 分享初始化器类
 */
class ShareInitializer {
  constructor() {
    this.initialized = false
    this.retryCount = 0
    this.maxRetries = 2 // 减少最大重试次数
    this.retryInterval = 3000 // 增加重试间隔
    this.currentShareData = null
    this.listenersInitialized = false
    this.lastRetryTime = 0 // 添加最后重试时间记录
  }

  /**
   * 立即配置分享（在页面加载时调用）
   * @param {Object} shareData 分享数据
   */
  async immediateInit(shareData = {}) {
    if (!isWechatBrowser()) {
      console.log('非微信环境，跳过分享配置')
      return false
    }

    // 防止频繁重试，但允许内容变化时的强制更新
    const now = Date.now()
    const oneMinute = 60 * 1000
    const isContentChange = shareData && shareData.title && 
      this.currentShareData && 
      shareData.title !== this.currentShareData.title;
    
    if (this.lastRetryTime && (now - this.lastRetryTime) < oneMinute && !isContentChange) {
      console.log('ShareInitializer: 1分钟内已尝试过，跳过重复初始化')
      return this.initialized
    }

    // 处理分享链接，确保与当前URL完全一致
    const processShareLink = (link) => {
      // 直接返回原始链接，不进行任何修改
      return link || window.location.href;
    };

    // 确保图片URL是绝对路径
    const ensureAbsoluteUrl = (url) => {
      if (!url) return 'https://mall.sainbainu.com/static/images/sharelogo.png';
      if (url.startsWith('http')) return url;
      if (url.startsWith('/')) return `${window.location.origin}${url}`;
      return `${window.location.origin}/${url}`;
    };

    // 每次都获取最新的页面特定默认配置
    const currentDefaultConfig = getPageSpecificDefaultConfig();
    // 合并默认配置和传入的配置
    const finalShareData = {
      ...currentDefaultConfig,
      ...shareData,
      // 确保链接是处理过的
      link: processShareLink(shareData.link || window.location.href),
      // 确保图片URL是绝对路径
      imgUrl: ensureAbsoluteUrl(shareData.imgUrl || currentDefaultConfig.imgUrl),
      // 确保描述字段正确处理
      desc: shareData.desc || shareData.description || currentDefaultConfig.desc
    };

    this.currentShareData = finalShareData;
    this.lastRetryTime = now; // 记录当前时间

    console.log('开始立即配置分享:', finalShareData);

    // 1. 立即更新meta标签 - 这是最关键的步骤
    this.updatePageMeta(finalShareData);

    // 2. 尝试配置微信JS-SDK分享 - 不等待，立即执行
    try {
      const result = await setupWechatShareWithRetry(finalShareData, 2);
      if (result) {
        this.initialized = true;
        console.log('立即分享配置成功');
        return true;
      }
    } catch (error) {
      console.warn('立即分享配置失败，将启动重试机制:', error.message);
    }

    // 3. 如果立即配置失败，启动重试机制
    this.startRetryMechanism();
    return false;
  }

  /**
   * 更新页面meta标签
   * @param {Object} shareData 分享数据
   */
  updatePageMeta(shareData) {
    // 更新页面标题
    document.title = shareData.title;

    // 确保图片URL是绝对路径
    const ensureAbsoluteUrl = (url) => {
      if (!url) return 'https://mall.sainbainu.com/static/images/sharelogo.png';
      if (url.startsWith('http')) return url;
      if (url.startsWith('/')) return `${window.location.origin}${url}`;
      return `${window.location.origin}/${url}`;
    };

    const imgUrl = ensureAbsoluteUrl(shareData.imgUrl);

    // 更新meta标签
    updateMetaTags({
      title: shareData.title,
      description: shareData.desc,
      image: imgUrl,
      url: shareData.link,
      type: 'website'
    });

    // 添加微信专用meta标签
    this.addWechatSpecificMeta({
      ...shareData,
      imgUrl: imgUrl
    });

    // 添加额外的微信分享标签
    this.addExtraWechatMeta({
      ...shareData,
      imgUrl: imgUrl
    });
    
    // 强制刷新meta标签
    this.forceMetaRefresh();
  }
  
  /**
   * 强制刷新meta标签，确保微信能够读取到
   */
  forceMetaRefresh() {
    // 添加一个临时注释节点，强制浏览器重新计算DOM
    const tempComment = document.createComment('Force meta refresh for WeChat');
    document.head.appendChild(tempComment);
    
    // 延迟移除，确保DOM已更新
    setTimeout(() => {
      document.head.removeChild(tempComment);
    }, 10);
    
    // 对于微信浏览器，添加额外的刷新机制
    if (isWechatBrowser()) {
      // 创建一个隐藏的iframe来触发微信的meta标签刷新
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = 'about:blank';
      document.body.appendChild(iframe);
      
      // 短暂延迟后移除iframe
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 100);
    }
  }

  /**
   * 添加微信专用meta标签
   * @param {Object} shareData 分享数据
   */
  addWechatSpecificMeta(shareData) {
    const metaTags = [
      { name: 'wechat:title', content: shareData.title },
      { name: 'wechat:description', content: shareData.desc },
      { name: 'wechat:image', content: shareData.imgUrl },
      { name: 'wechat:timeline_title', content: shareData.title },
      { name: 'wechat:appmsg_title', content: shareData.title },
      { name: 'wechat:appmsg_desc', content: shareData.desc },
      // 添加更多微信专用标签
      { name: 'wechat:card', content: 'summary_large_image' },
      { name: 'wechat:share:title', content: shareData.title },
      { name: 'wechat:share:description', content: shareData.desc },
      { name: 'wechat:share:img', content: shareData.imgUrl },
      { name: 'wechat:share:url', content: shareData.link }
    ];

    metaTags.forEach(({ name, content }) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    });
  }

  /**
   * 添加额外的微信分享meta标签
   * @param {Object} shareData 分享数据
   */
  addExtraWechatMeta(shareData) {
    // 添加Open Graph标签（微信也会读取这些标签）
    const ogTags = [
      { property: 'og:title', content: shareData.title },
      { property: 'og:description', content: shareData.desc },
      { property: 'og:image', content: shareData.imgUrl },
      { property: 'og:url', content: shareData.link },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: '蒙古丽人商城' },
      // 图片尺寸信息
      { property: 'og:image:width', content: '300' },
      { property: 'og:image:height', content: '300' },
      { property: 'og:image:alt', content: shareData.title }
    ];

    ogTags.forEach(({ property, content }) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });

    // 添加itemprop标签（微信也会读取这些标签）
    const itemPropTags = [
      { itemprop: 'name', content: shareData.title },
      { itemprop: 'description', content: shareData.desc },
      { itemprop: 'image', content: shareData.imgUrl }
    ];

    itemPropTags.forEach(({ itemprop, content }) => {
      let meta = document.querySelector(`meta[itemprop="${itemprop}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('itemprop', itemprop);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });
  }

  /**
   * 启动重试机制
   */
  startRetryMechanism() {
    if (this.initialized || this.retryCount >= this.maxRetries) {
      return;
    }

    console.log(`启动分享配置重试，第 ${this.retryCount + 1} 次`);

    // 使用递减的重试间隔，前几次快速重试
    const retryDelay = Math.max(500, this.retryInterval - (this.retryCount * 300));

    setTimeout(async () => {
      this.retryCount++;

      // 每次重试前重新更新meta标签
      this.updatePageMeta(this.currentShareData);

      try {
        const result = await setupWechatShareWithRetry(this.currentShareData, 1);
        if (result) {
          this.initialized = true;
          console.log(`分享配置重试成功，第 ${this.retryCount} 次`);
          return;
        }
      } catch (error) {
        console.warn(`分享配置重试失败，第 ${this.retryCount} 次:`, error.message);
      }

      // 继续重试
      this.startRetryMechanism();
    }, retryDelay);
  }

  /**
   * 监听页面可见性变化，在页面重新可见时重新配置分享
   */
  initVisibilityListener() {
    if (typeof document.hidden !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && !this.initialized && this.currentShareData) {
          console.log('页面重新可见，尝试重新配置分享')
          this.immediateInit(this.currentShareData)
        }
      })
    }
  }

  /**
   * 监听微信ready事件
   */
  initWechatReadyListener() {
    if (window.wx) {
      window.wx.ready(() => {
        if (!this.initialized && this.currentShareData) {
          console.log('微信JS-SDK ready，重新配置分享')
          this.immediateInit(this.currentShareData)
        }
      })
    } else {
      // 如果wx还没加载，等待加载
      const checkWx = setInterval(() => {
        if (window.wx) {
          clearInterval(checkWx)
          this.initWechatReadyListener()
        }
      }, 500)
    }
  }

  /**
   * 监听路由变化，在路由变化时重新配置分享
   */
  initRouteListener() {
    // 动态导入路由，避免循环依赖
    import('@/router').then(({ default: router }) => {
      router.afterEach(() => {
        console.log('路由变化，重新配置分享...')
        
        // 更新当前分享数据的链接
        if (this.currentShareData) {
          const updatedShareData = {
            ...this.currentShareData,
            link: window.location.href
          }
          
          // 重新初始化分享配置
          this.immediateInit(updatedShareData).catch(error => {
            console.error('路由变化后分享配置失败:', error)
          })
        }
      })
    }).catch(error => {
      console.warn('无法导入路由模块，跳过路由监听:', error)
    })
  }

  /**
   * 完整初始化（包含所有监听器）
   * @param {Object} shareData 分享数据
   */
  async fullInit(shareData = {}) {
    // 立即配置
    await this.immediateInit(shareData)

    // 初始化监听器（只在第一次初始化时添加，避免重复监听）
    if (!this.listenersInitialized) {
      this.initVisibilityListener()
      this.initWechatReadyListener()
      this.initRouteListener()
      this.listenersInitialized = true
      console.log('分享监听器初始化完成')
    }

    console.log('分享初始化器完整初始化完成')
  }
}

// 创建全局实例
const shareInitializer = new ShareInitializer()

/**
 * 快速初始化分享
 * @param {Object} shareData 分享数据
 */
export async function quickInitShare(shareData = {}) {
  return await shareInitializer.immediateInit(shareData)
}

/**
 * 完整初始化分享（推荐使用）
 * @param {Object} shareData 分享数据
 */
export async function fullInitShare(shareData = {}) {
  return await shareInitializer.fullInit(shareData)
}

/**
 * 更新分享配置（轻量级更新）
 * @param {Object} shareData 新的分享数据
 */
export async function updateShareConfig(shareData) {
  if (!isWechatBrowser()) {
    console.log('非微信环境，跳过分享配置更新')
    return false
  }

  // 强制更新标志 - 当页面内容发生变化时，需要强制更新分享配置
  const forceUpdate = shareData && shareData.title && 
    shareInitializer.currentShareData && 
    shareData.title !== shareInitializer.currentShareData.title;

  // 如果还没有初始化或需要强制更新，则执行完整初始化
  if (!shareInitializer.initialized || forceUpdate) {
    console.log(forceUpdate ? '检测到内容变化，强制更新分享配置' : '分享未初始化，执行完整初始化')
    return await shareInitializer.immediateInit(shareData)
  }

  // 轻量级更新：只更新配置，不重复初始化
  console.log('执行轻量级分享配置更新:', shareData)
  
  // 处理分享链接，确保与当前URL完全一致
  const processShareLink = (link) => {
    return link || window.location.href;
  };

  // 确保图片URL是绝对路径
  const ensureAbsoluteUrl = (url) => {
    if (!url) return 'https://mall.sainbainu.com/static/images/sharelogo.png';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/')) return `${window.location.origin}${url}`;
    return `${window.location.origin}/${url}`;
  };

  // 每次都获取最新的页面特定默认配置
  const currentDefaultConfig = getPageSpecificDefaultConfig();
  // 合并当前配置和新配置
  const finalShareData = {
    ...currentDefaultConfig,
    ...shareData,
    link: processShareLink(shareData.link || window.location.href),
    imgUrl: ensureAbsoluteUrl(shareData.imgUrl || currentDefaultConfig.imgUrl),
    // 确保描述字段正确处理
    desc: shareData.desc || shareData.description || currentDefaultConfig.desc
  };

  // 更新当前分享数据
  shareInitializer.currentShareData = finalShareData;

  // 只更新meta标签，不重新初始化JS-SDK
  shareInitializer.updatePageMeta(finalShareData);

  // 如果微信JS-SDK已经ready，则更新配置
  if (window.wx && window.wx.ready) {
    try {
      await setupWechatShareWithRetry(finalShareData, 1);
      console.log('分享配置更新成功')
      return true
    } catch (error) {
      console.warn('分享配置更新失败:', error.message)
      return false
    }
  }

  console.log('微信JS-SDK未ready，仅更新meta标签')
  return true
}

export default shareInitializer